/**
 * Onboarding data, ported verbatim from the mobile app's OnboardingFlow.tsx.
 * Single source of truth so web and mobile capture identical survey signals.
 *
 * If the mobile app updates these lists, mirror the change here so the
 * `profiles` row shape stays consistent across surfaces.
 */

// 6-screen sequence, mirrors `app/(tabs)/onboarding.tsx` state machine.
// Note: iOS' state machine also defines a `fullthrottle` final screen,
// but the iOS `handleFollowUpAnswer` path skips it (Apple Guideline
// 5.1.1(v), don't gate the dashboard behind a paywall during onboarding).
// We follow the same path on web, last follow-up question → smart
// routing → dashboard / gate / paywall.
export const SCREENS = [
  'communityGroups',
  'values',
  'projectType',
  'stateLocation',
  'buildingStage',
  'followUp',
];

export const VALUE_SCREENS = [
  {
    headline: 'Track Everything',
    body: 'Keep your budget, milestones and documents in one place. Nothing gets missed. Nothing gets forgotten.',
  },
  {
    headline: 'Manage Smarter',
    body: 'Understand quotes, inclusions and key decisions clearly. Avoid confusion before it becomes expensive.',
  },
  {
    headline: 'Take Control',
    body: 'Know what stage you are in and what comes next. Stay ahead instead of reacting late.',
  },
  {
    headline: 'BuildPilot Intelligence',
    body: 'Unlock deeper guidance to review quotes, visualise designs and spot issues early.',
  },
];

export const PROJECT_TYPES = [
  { value: 'New Home', description: 'Building from scratch' },
  { value: 'Renovation', description: 'Major changes to existing home' },
  { value: 'Deck', description: 'Timber or composite deck' },
  { value: 'Pergola', description: 'Pergola, patio, or verandah' },
  { value: 'Pool', description: 'Swimming pool or spa' },
  { value: 'Landscaping', description: 'Garden & outdoor design' },
  { value: 'Shed / Garage', description: 'Shed, garage, or carport' },
  { value: 'Interior', description: 'Interior design & styling' },
  { value: 'Other', description: 'Other building project' },
];

export const AUSTRALIAN_STATES = [
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'SA', label: 'South Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'ACT', label: 'Australian Capital Territory' },
  { value: 'NT', label: 'Northern Territory' },
];

export const BUILDING_STAGES = {
  'New Home': [
    'Thinking of building',
    'Signed up to a builder',
    'Construction Drawings/Master Plans',
    'Engineering',
    'Energy Rating',
    'Building Costs',
    'Council Approvals',
    'Selections',
    'Slab Pour',
    'Frame Stage',
    'Lockup Stage',
    'First Fix',
    'Insulation and Linings',
    'Second Fix',
    'Practical Completion',
    'Living in - First 30 Days',
    'Maintenance Period',
  ],
  'Renovation': [
    'Thinking of building',
    'Signed up to a builder',
    'Site Measure and Plans Reviewed',
    'Renovation Drawings/Updated Layout',
    'Engineering (if structural)',
    'Energy Rating (if required)',
    'Final Building Costs',
    'Council Approval (if required)',
    'Selections',
    'Demolition',
    'Structural Changes/Framing',
    'Rough In (Plumbing & Electrical)',
    'Insulation & Linings',
    'Waterproofing (wet areas)',
    'Cabinetry & Tiling',
    'Second Fix',
    'Practical Completion',
    'Living in - First 30 Days',
    'Maintenance Period',
  ],
  // Australian-construction-realistic stages per build type. Researched
  // against the local building industry process (Feb 2026), pergolas
  // and decks don't always need engineering or slabs; pools have
  // mandatory AS1926 fencing before fill; landscaping rarely needs
  // approvals. Replaces the previous generic Home Improvement fallback
  // (which iOS still uses today, web is the source of truth here).
  // Per user direction (Feb 26, 2026): Decks DON'T need a separate
  // "Final Inspection & Handover" milestone or a 12-month "Maintenance
  // Period", the final-finishing step (oil/stain) IS effectively the
  // handover for a deck, and deck warranties are timber-product based
  // rather than a builder-warranty period. Final stage list = 10.
  'Deck': [
    'Thinking of building',
    'Design & Material Selection',
    'Quotes & Builder Selection',
    'Council Approval (if required)',
    'Site Preparation',
    'Footings',
    'Subframe & Bearers',
    'Decking Boards Laid',
    'Balustrades & Stairs',
    'Finishing (Oil/Stain)',
  ],
  // Per user direction (Feb 26, 2026): pergolas don't have a builder-
  // warranty period or a separate handover milestone, roofing/install
  // sign-off IS the handover. Final stage list = 10.
  'Pergola': [
    'Thinking of building',
    'Design & Material Selection',
    'Quotes & Builder Selection',
    'Council Approval (if required)',
    'Site Preparation',
    'Post Footings',
    'Posts Installed',
    'Beams & Rafters',
    'Roofing (Colorbond/Polycarb/Louvre)',
    'Electrical & Lighting (if any)',
  ],
  // Per user direction (Feb 26, 2026): pool builders treat the
  // post-fill commissioning + 30-day water-balance period as part of
  // handover, so we collapse those into "Fill, Commission & Handover"
  // and drop the separate "First 30 Days" + "Maintenance Period"
  // milestones. Final stage list = 14.
  'Pool': [
    'Thinking of building',
    'Design & Pool Type Selection',
    'Quotes & Pool Builder Selection',
    'Engineering',
    'Council Development Approval',
    'Site Preparation & Access',
    'Excavation',
    'Pool Shell Installation',
    'Plumbing & Equipment',
    'Electrical & Lighting',
    'Coping & Paving',
    'Interior Finish & Tiling',
    'Pool Fencing (AS1926 Compliant)',
    'Fill, Commission & Handover',
  ],
  'Landscaping': [
    'Thinking of building',
    'Design & Site Assessment',
    'Quotes & Landscaper Selection',
    'Council Approval (if required)',
    'Site Clearing & Earthworks',
    'Drainage & Irrigation',
    'Hardscape (Paving, Retaining Walls)',
    'Soil Preparation',
    'Planting & Turf',
    'Lighting & Features',
    'Mulching & Finishing',
    'Establishment Period',
    'Maintenance Period',
  ],
  'Shed / Garage': [
    'Thinking of building',
    'Design & Kit Selection',
    'Quotes & Builder Selection',
    'Engineering',
    'Council Approval (if required)',
    'Site Preparation',
    'Concrete Slab Pour',
    'Frame Erected',
    'Wall & Roof Cladding',
    'Doors & Windows',
    'Electrical Fit-out',
    'Internal Lining (if applicable)',
    'Final Inspection & Handover',
    'Maintenance Period',
  ],
  // Legacy generic Home Improvement flow, still used by:
  //   • Interior project type (no construction stages, kept generic)
  //   • Other project type (catch-all)
  //   • Any unknown project type (final fallback)
  'Home Improvement': [
    'Thinking of building',
    'Signed up to a contractor',
    'Design/Plans',
    'Engineering (if required)',
    'Final Building Costs',
    'Council Approval (if required)',
    'Selections',
    'Site Preparation',
    'Footings/Base/Slab',
    'Installation Stage',
    'Services Connection (if required)',
    'Finishing Stage',
    'Practical Completion',
    'First 30 Days',
    'Maintenance Period',
  ],
};

// Aliases, same flow under multiple display labels so the lookup is
// resilient to onboarding-survey casing / spelling variations.
BUILDING_STAGES['Shed/Garage'] = BUILDING_STAGES['Shed / Garage'];
BUILDING_STAGES['Decking'] = BUILDING_STAGES['Deck'];

// All non-{New Home, Renovation, Deck, Pergola, Pool, Landscaping,
// Shed / Garage} project types use the Home Improvement stages.
export const getStagesForProjectType = (projectType) => {
  if (BUILDING_STAGES[projectType]) return BUILDING_STAGES[projectType];
  return BUILDING_STAGES['Home Improvement'];
};

export const FOLLOW_UP_QUESTIONS = [
  {
    key: 'challenge',
    question: 'What feels most challenging right now?',
    options: [
      'Understanding pricing and quotes',
      'Knowing what should be included',
      'Comparing builders fairly',
      'Making design decisions',
      'Staying within budget',
      'Knowing what comes next',
    ],
  },
  {
    key: 'confidence',
    question: 'How confident do you feel about your decisions?',
    options: ['Very confident', 'Somewhat confident', 'Unsure', 'Not confident at all'],
  },
  {
    key: 'help_needed',
    question: 'What would help you most right now?',
    options: [
      'Clear next steps',
      'Better budget visibility',
      'Help reviewing quotes',
      'Design guidance',
      'Avoiding costly mistakes',
      'Ongoing support',
    ],
  },
];
